import { forwardRef } from 'react';
import { css, cx, resetButtonStyle } from '@eti/styles';
import type { BreakpointsTypes, SpacingTypes } from '@etg/wings';
import type { PolymorphicRef } from '@etg/wings/dist/types/polymorphicTypes';
import { Card, vars } from '@etg/wings';
import type {
  ComponentPropsWithoutRef,
  ElementType,
  PropsWithChildren,
  ReactNode,
  MouseEvent,
} from 'react';
import { focusStyles } from '../../utils/styleUtils';

const buttonStyles = css`
  ${resetButtonStyle}; /* stylelint-disable-next-line */
  border: 1px solid ${vars.colors.divider};
  border-radius: ${vars.card.borderRadius};
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
  text-align: start;
  width: 100%;

  :active {
    border-color: ${vars.colors.inputs.main};
  }

  :active,
  :hover,
  :hover > div {
    background-color: ${vars.colors.inputs.hover};
  }
`;

const cardStyles = css`
  height: 100%;
`;

type AsProp<C extends ElementType> = {
  as?: C;
};
type PropsToOmit<C extends ElementType, P> = keyof (AsProp<C> & P);
type PolymorphicComponentProp<C extends ElementType, Props = {}> = PropsWithChildren<
  Props & AsProp<C> & Omit<ComponentPropsWithoutRef<C>, PropsToOmit<C, Props>>
>;

interface ClickableCardProps {
  children?: ReactNode;
  className?: string;
  onCardSelection?: (event?: MouseEvent<HTMLButtonElement>) => void;
  'data-testid'?: string;
  padding?: SpacingTypes | Partial<Record<BreakpointsTypes, SpacingTypes>>;
}

const ClickableCard = forwardRef(
  <C extends ElementType = 'button'>(
    {
      as,
      children,
      className,
      onCardSelection,
      'data-testid': dataTestId,
      padding = 0,
      type,
      ...restProps
    }: PolymorphicComponentProp<C, ClickableCardProps>,
    ref?: PolymorphicRef<C>,
  ) => {
    const Component = as || 'button';

    return (
      <Component
        className={cx(buttonStyles, focusStyles, className)}
        data-testid={dataTestId}
        onClick={onCardSelection}
        ref={ref}
        type={type ?? (as ? undefined : 'button')}
        {...restProps}
      >
        <Card className={cardStyles} padding={padding}>
          {children}
        </Card>
      </Component>
    );
  },
);

export default ClickableCard;
